import React, { useMemo } from 'react'
import { graphql } from 'gatsby'

import { Paragraph, ParagraphProps } from './types'
import { isSSR } from '../ui/utils'

import Actions from './actions'
import Button from './button'
import ButtonGroup from './button_group'
import Carousel from './carousel'
import Form from './form'
import FormCount from './form_count'
import Gallery from './gallery'
import HeroContent from './hero_content'
import HeroForm from './hero_form'
import HeroVideo from './hero_video'
import MainPoint from './main_point'
import Media from './media'
import TextParagraph from './paragraph'
import PromotedItems from './promoted_items'
import RecentArticles from './recent_articles'

/**
 * Paragraphs are implemented with loadable components.
 *
 * Most of the paragraphs anyway. Some small paragraphs that don't have
 * external dependencies should not use ProxyComponent
 */
const ParagraphLibrary: { [key: string]: React.FC<ParagraphProps<any> & { fallback?: JSX.Element }> } = {
	paragraph__actions: Actions,
	paragraph__hero_content: HeroContent,
	paragraph__hero_form: HeroForm,
	paragraph__hero_video: HeroVideo,
	paragraph__paragraph: TextParagraph,
	paragraph__button: Button,
	paragraph__media: Media,
	paragraph__main_point: MainPoint,
	paragraph__gallery: Gallery,
	paragraph__form: Form,
	paragraph__form_count: FormCount,
	paragraph__carousel: Carousel,
	paragraph__button_group: ButtonGroup,
	paragraph__promoted_items: PromotedItems,
	paragraph__recent_articles: RecentArticles
}

export interface Props {
	paragraphs: Paragraph[]
}

function paragraphId(paragraph: Paragraph) {
	return `pg-${paragraph.drupal_internal__id}`
}

function dynamicRender(paragraph: Paragraph, fallback?: JSX.Element) {
	// https://github.com/hector-del-rio/gatsby-plugin-loadable-components-ssr/issues/6#issuecomment-687538596
	const Component = ParagraphLibrary[paragraph.__typename]

	return (
		<section id={paragraphId(paragraph)} key={paragraph.drupal_internal__id}>
			<Component paragraph={paragraph} />
		</section>
	)
}

function staticRender(paragraph: Paragraph) {
	const element = document.getElementById(paragraphId(paragraph))

	if (element) {
		return (
			// Frament doesn't support dangerouslySetInnerHTML so this must be always wrapped in an element.
			// See: https://github.com/reactjs/rfcs/pull/129
			<section
				id={paragraphId(paragraph)}
				key={paragraph.drupal_internal__id}
				dangerouslySetInnerHTML={{ __html: element.innerHTML }}
			/>
		)
	}

	return undefined
}

const Paragraphs: React.FC<Props> = ({ paragraphs }) => {
	const renderedParagraphs = useMemo(
		() =>
			paragraphs.map(paragraph => {
				if (isSSR()) {
					return dynamicRender(paragraph)
				}

				const hasJS = true
				const wasUserPreviouslyOnSite = window.history.state
				const fallback = staticRender(paragraph)

				if (fallback && !hasJS && !wasUserPreviouslyOnSite) {
					return fallback
				}

				return dynamicRender(paragraph, fallback)
			}),
		paragraphs
	)

	return <>{renderedParagraphs}</>
}

export default Paragraphs

export const query = graphql`
	fragment Paragraphs on paragraphs_union {
		__typename
		... on paragraph__actions {
			...ActionsParagraph
		}
		... on paragraph__hero_content {
			...HeroContentParagraph
		}
		... on paragraph__hero_form {
			...HeroFormParagraph
		}
		... on paragraph__hero_video {
			...HeroVideoParagraph
		}
		... on paragraph__paragraph {
			...TextParagraph
		}
		... on paragraph__button {
			...ButtonParagraph
		}
		... on paragraph__button_group {
			...ButtonGroupParagraph
		}
		... on paragraph__media {
			...MediaParagraph
		}
		... on paragraph__main_point {
			...MainPointParagraph
		}
		... on paragraph__gallery {
			...GalleryParagraph
		}
		... on paragraph__form {
			...FormParagraph
		}
		... on paragraph__form_count {
			...FormCountParagraph
		}
		... on paragraph__carousel {
			...CarouselParagraph
		}
		... on paragraph__promoted_items {
			...PromotedItemsParagraph
		}
		... on paragraph__recent_articles {
			...RecentArticlesParagraph
		}
	}
`
